import Dexie, { type Table } from 'dexie';
import type {
  Workspace,
  Capability,
  CapabilityNode,
  Edge,
  Collection,
  SyncEvent,
  Plugin,
  ActivityEvent,
} from '@/types';

// ─── Database schema version ──────────────────────────────────────────────────

const DB_NAME = 'capabilityos';
const DB_VERSION = 1;

// ─── CapOS Dexie database ─────────────────────────────────────────────────────

export class CapOSDatabase extends Dexie {
  workspaces!: Table<Workspace, string>;
  capabilities!: Table<Capability, string>;
  nodes!: Table<CapabilityNode, string>;
  edges!: Table<Edge, string>;
  collections!: Table<Collection, string>;
  plugins!: Table<Plugin, string>;
  syncQueue!: Table<SyncEvent, string>;
  activityFeed!: Table<ActivityEvent, string>;

  constructor() {
    super(DB_NAME);

    this.version(DB_VERSION).stores({
      // Primary key + indexes
      workspaces: 'id, slug, ownerId, updatedAt',

      capabilities:
        'id, workspaceId, type, status, *tags, createdAt, updatedAt, createdBy, [workspaceId+updatedAt]',

      nodes:
        'id, capabilityId, workspaceId, updatedAt, [workspaceId+capabilityId]',

      edges:
        'id, workspaceId, sourceNodeId, targetNodeId, type, updatedAt, [workspaceId+updatedAt]',

      collections:
        'id, workspaceId, parentId, createdBy, updatedAt, [workspaceId+updatedAt]',

      plugins:
        'id, workspaceId, status, updatedAt',

      syncQueue:
        'id, workspaceId, entityType, entityId, status, operation, createdAt, [workspaceId+status]',

      activityFeed:
        'id, workspaceId, userId, action, entityId, createdAt, [workspaceId+createdAt]',
    });
  }
}

// ─── Singleton instance ───────────────────────────────────────────────────────

export const db = new CapOSDatabase();

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Upsert a single record (insert or replace based on primary key).
 */
export async function upsertRecord<T extends { id: string }>(
  table: Table<T, string>,
  record: T
): Promise<string> {
  return table.put(record);
}

/**
 * Bulk upsert an array of records.
 */
export async function bulkUpsert<T extends { id: string }>(
  table: Table<T, string>,
  records: T[]
): Promise<void> {
  await table.bulkPut(records);
}

/**
 * Get all capabilities for a workspace, sorted by updatedAt descending.
 */
export async function getCapabilitiesForWorkspace(
  workspaceId: string,
  limit = 200
): Promise<Capability[]> {
  return db.capabilities
    .where('workspaceId')
    .equals(workspaceId)
    .reverse()
    .sortBy('updatedAt')
    .then((items) => items.slice(0, limit));
}

/**
 * Get all graph nodes for a workspace.
 */
export async function getNodesForWorkspace(workspaceId: string): Promise<CapabilityNode[]> {
  return db.nodes.where('workspaceId').equals(workspaceId).toArray();
}

/**
 * Get all edges for a workspace.
 */
export async function getEdgesForWorkspace(workspaceId: string): Promise<Edge[]> {
  return db.edges.where('workspaceId').equals(workspaceId).toArray();
}

/**
 * Get all pending sync events, ordered oldest first.
 */
export async function getPendingSyncEvents(workspaceId: string): Promise<SyncEvent[]> {
  return db.syncQueue
    .where('[workspaceId+status]')
    .equals([workspaceId, 'pending'])
    .sortBy('createdAt');
}

/**
 * Enqueue a sync event.
 */
export async function enqueueSyncEvent(event: SyncEvent): Promise<void> {
  await db.syncQueue.put(event);
}

/**
 * Mark sync events as synced.
 */
export async function markSyncEventsComplete(ids: string[]): Promise<void> {
  const now = new Date().toISOString();
  await db.syncQueue.bulkPut(
    await db.syncQueue.bulkGet(ids).then((items) =>
      items
        .filter((item): item is SyncEvent => item !== undefined)
        .map((item) => ({ ...item, status: 'synced' as const, syncedAt: now }))
    )
  );
}

/**
 * Clear all data for a workspace (e.g., on sign-out or workspace switch).
 */
export async function clearWorkspaceData(workspaceId: string): Promise<void> {
  await db.transaction(
    'rw',
    [db.capabilities, db.nodes, db.edges, db.collections, db.plugins, db.syncQueue, db.activityFeed],
    async () => {
      await db.capabilities.where('workspaceId').equals(workspaceId).delete();
      await db.nodes.where('workspaceId').equals(workspaceId).delete();
      await db.edges.where('workspaceId').equals(workspaceId).delete();
      await db.collections.where('workspaceId').equals(workspaceId).delete();
      await db.plugins.where('workspaceId').equals(workspaceId).delete();
      await db.syncQueue.where('workspaceId').equals(workspaceId).delete();
      await db.activityFeed.where('workspaceId').equals(workspaceId).delete();
    }
  );
}

/**
 * Full-text search on capability title and summary (client-side).
 */
export async function searchCapabilitiesLocally(
  workspaceId: string,
  query: string
): Promise<Capability[]> {
  const q = query.toLowerCase().trim();
  if (!q) {
    return getCapabilitiesForWorkspace(workspaceId);
  }

  return db.capabilities
    .where('workspaceId')
    .equals(workspaceId)
    .filter((cap) => {
      const titleMatch = cap.title.toLowerCase().includes(q);
      const summaryMatch = cap.summary?.toLowerCase().includes(q) ?? false;
      const tagMatch = cap.tags.some((t) => t.toLowerCase().includes(q));
      return titleMatch || summaryMatch || tagMatch;
    })
    .toArray();
}
